import React, { useCallback, useEffect, useRef, useState } from 'react';
import KakaoIcon from '../components/KakaoIcon';

/**
 * **티어표 탭** — 점령전 5성·4성 공격·방어 S~D.
 *
 * 처음엔 AI 가 매긴 고정 표였다(사용자 2026-10-29). **2026-10-31부터 길드원 모두가 몹을 끌어 옮겨 고친다**(사용자 "ai가 평가하는
 * 게 아니라 유저 모두가 끌어당겨서 수정할 수 있도록") — 표는 하나를 모두가 같이 쓴다(서버 `utils/tierList.ts`). 표에 없는 몹은
 * 맨 아래 **미분류** 에 있고, 거기서 끌어다 넣는다(표에서 미분류로 끌어 빼기도 된다).
 *
 * 끌기는 마우스도 폰도 **누른 채 조금 움직이면 바로** 시작한다(사용자 2026-10-31 "꾹 누르지 않아도 드래그하면 옮겨지게" — 처음엔
 * 폰은 0.3초 꾹 눌러야 했다). 그래서 몹 그림 위에서는 화면이 굴러가지 않는다(`touch-action: none`) — 화면은 몹 사이 빈 곳·줄 머리를
 * 잡고 굴린다. 짧게 누르면 예전처럼 이름·우리 전적 말풍선이 뜬다.
 *
 * [카톡 공유](공지 탭과 같은 노란 말풍선 아이콘)를 누르면 설탕봇이 지금 보는 표(공격·방어 × 별)의 링크를 카톡 방에 보낸다
 * (길드 단톡방). 그 링크의 카톡 미리보기가 표 그림이다(서버 `/og/tier/...png`). 링크를 누르면 이 탭이 그 표로 열린다.
 */
type Mode = 'attack' | 'defense';
/** 3 = 2각·3성 표(사용자 2026-10-31 "2각 몹이랑 3성 중에서 많이 쓰는 친구들만 추가") */
type Stars = 5 | 4 | 3;
type TierKey = 'S' | 'A' | 'B' | 'C' | 'D';
type Slot = TierKey | 'pool';
/** win·lose — 우리 전투기록 전적(공격은 우리 공덱, 방어는 방덱으로 나와 막았나). 등급과는 무관하다 */
interface Mob { id: number; label: string; filepath: string | null; win: number; lose: number }
type Tiers = Record<Slot, Mob[]>;
interface TierData { at: string; by: string | null; attack: Record<Stars, Tiers>; defense: Record<Stars, Tiers> }

const PURPLE = '#667eea';
/** 등급 칸 색 — 차분한 무지개에 D 는 회색(사용자 2026-10-31 초안 1번 + "D를 회색으로"). 카톡 그림(서버 tierList TIER_COLOR)과 같다 */
const TIERS: { key: TierKey; color: string }[] = [
  { key: 'S', color: '#c9505f' },
  { key: 'A', color: '#d9874a' },
  { key: 'B', color: '#c8a64a' },
  { key: 'C', color: '#5f9a6e' },
  { key: 'D', color: '#8a8f99' }
];

const Pill: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <button type="button" onClick={onClick} style={{
    border: `1.5px solid ${on ? PURPLE : '#d6d9e6'}`, background: on ? PURPLE : '#fff', color: on ? '#fff' : '#555',
    borderRadius: 999, padding: '6px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer'
  }}>{children}</button>
);

/**
 * **받은 표를 들고 있는다**(사용자 2026-10-29 "점령전 티어표 캐시되게해") — 탭을 다시 열면 기다리지 않고 바로 그린다.
 * 새로 고침한 뒤에도 지난번 표(브라우저 저장소)를 먼저 그리고, 서버 답이 오면 바꾼다. 저장소를 못 쓰면(사생활 창 등) 그냥 받는다.
 */
const STORE_KEY = 'tierList.v2';
let held: TierData | null = null;
/**
 * 받은 표를 고르게 맞춘다 — 빈 줄·미분류(pool)가 빠져 와도 빈 목록으로. 옛 서버(미분류를 안 주던 때)와 붙었을 때
 * `tiers.pool.length` 에서 화면이 통째로 멈췄다(사용자 2026-10-31 로컬 — 화면만 새 코드, 서버는 옛 코드).
 */
function normalize(d: any): TierData {
  for (const mode of ['attack', 'defense']) {
    d[mode] = d[mode] || {};
    for (const stars of [5, 4, 3]) {
      const t = (d[mode][stars] = d[mode][stars] || {});
      for (const k of ['S', 'A', 'B', 'C', 'D', 'pool']) if (!Array.isArray(t[k])) t[k] = [];
    }
  }
  return d as TierData;
}
function stored(): TierData | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? normalize(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}
function keep(d: TierData) {
  held = d;
  try { localStorage.setItem(STORE_KEY, JSON.stringify(d)); } catch { /* 저장소를 못 쓰면 이번 창에서만 들고 있는다 */ }
}

/** 공유 링크(`/tier/<mode>/<stars>`)로 들어왔으면 그 표로 연다 */
function fromPath(): { mode: Mode; stars: Stars } | null {
  const m = window.location.pathname.match(/^\/tier\/(attack|defense)\/(5|4|3)$/);
  return m ? { mode: m[1] as Mode, stars: Number(m[2]) as Stars } : null;
}

/** 표 안에서 몹 하나를 옮긴 결과(서버 답을 기다리지 않고 먼저 그린다) */
function moved(t: Tiers, label: string, to: Slot, index: number): Tiers {
  const next = {} as Tiers;
  let mob: Mob | undefined;
  (Object.keys(t) as Slot[]).forEach((k) => {
    next[k] = t[k].filter((m) => (m.label === label ? ((mob = m), false) : true));
  });
  if (!mob) return t;
  const list = next[to];
  list.splice(Math.max(0, Math.min(index, list.length)), 0, mob);
  return next;
}

const TierPage: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const start = fromPath();
  const [data, setData] = useState<TierData | null>(() => held || (held = stored()));
  const [error, setError] = useState('');
  const [mode, setMode] = useState<Mode>(start?.mode || 'attack');
  const [stars, setStars] = useState<Stars>(start?.stars || 5);
  const [picked, setPicked] = useState<Mob | null>(null);
  const [side, setSide] = useState<'left' | 'center' | 'right'>('center');
  const [poolOpen, setPoolOpen] = useState(false);
  const [toast, setToast] = useState('');
  // 끄는 중 — 무엇을, 손가락 자리, 놓을 자리
  const [drag, setDrag] = useState<{ mob: Mob; x: number; y: number; to: Slot | null; index: number } | null>(null);
  const press = useRef<{ mob: Mob; from: Slot; x: number; y: number; started: boolean } | null>(null);
  const saving = useRef(false);
  // 끌기를 막 끝낸 손 — 이어 오는 click 이 말풍선을 띄우지 않게
  const justDragged = useRef(false);

  const load = useCallback(() => {
    if (saving.current) return;
    fetch('/api/siege-battles/tier-list', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) { if (!held) setError(d.error || '티어표를 불러오지 못했습니다'); return; }
        if (saving.current) return;
        const n = normalize(d);
        keep(n);
        setData(n);
      })
      .catch(() => { if (!held) setError('티어표를 불러오지 못했습니다'); });
  }, []);

  /*
   * 처음 한 번 + **실시간**(사용자 2026-10-31 "실시간으로 옮기면 다른 사람들도 실시간으로 보이게") — 누가 옮기면 서버가 알려 주고
   * (`/tier-list/stream`, 서버 보냄 이벤트) 그때 표를 새로 읽는다. 알림이 끊긴 때(프록시·잠든 폰)를 위해 30초마다도 읽는다.
   */
  useEffect(() => {
    load();
    const t = window.setInterval(load, 30000);
    let es: EventSource | null = null;
    try {
      es = new EventSource('/api/siege-battles/tier-list/stream', { withCredentials: true });
      es.onmessage = () => load();
    } catch { /* 이벤트를 못 쓰는 브라우저 — 30초 읽기로 버틴다 */ }
    // 잠들었던 폰이 깨어나면 바로 한 번 읽는다
    const wake = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', wake);
    return () => { window.clearInterval(t); es?.close(); document.removeEventListener('visibilitychange', wake); };
  }, [load]);

  useEffect(() => { setPicked(null); }, [mode, stars]);

  // 말풍선은 바깥 아무 데나 누르면 닫힌다(몹을 누르면 그 몹 단추가 먼저 받아 막는다)
  useEffect(() => {
    if (!picked) return;
    const close = () => setPicked(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [picked]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(''), 2500);
    return () => window.clearTimeout(t);
  }, [toast]);

  /** 손가락·마우스 자리에서 놓을 줄과 순서를 찾는다 — 그 줄 몹들 중 손가락보다 뒤에 있는 첫 몹 자리 */
  const dropAt = (x: number, y: number): { to: Slot | null; index: number } => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const row = el?.closest('[data-tier-row]') as HTMLElement | null;
    if (!row) return { to: null, index: 0 };
    const to = row.dataset.tierRow as Slot;
    const cells = Array.from(row.querySelectorAll<HTMLElement>('[data-mob]')).filter((c) => c.dataset.mob !== press.current?.mob.label);
    let index = cells.length;
    for (let i = 0; i < cells.length; i++) {
      const r = cells[i].getBoundingClientRect();
      if (y < r.top || (y <= r.bottom && x < r.left + r.width / 2)) { index = i; break; }
    }
    return { to, index };
  };

  // 폰에서 끄는 동안은 화면이 굴러가지 않게 막는다(몹 그림의 touch-action: none 과 함께 — 끌다가 빈 곳을 지나도 굴러가지 않게)
  useEffect(() => {
    if (!drag) return;
    const stop = (e: TouchEvent) => e.preventDefault();
    document.addEventListener('touchmove', stop, { passive: false });
    return () => document.removeEventListener('touchmove', stop);
  }, [!!drag]); // eslint-disable-line react-hooks/exhaustive-deps

  const beginDrag = (x: number, y: number) => {
    const p = press.current;
    if (!p) return;
    p.started = true;
    setPicked(null);
    setDrag({ mob: p.mob, x, y, ...dropAt(x, y) });
  };

  const onDown = (e: React.PointerEvent, mob: Mob, from: Slot) => {
    if (e.button !== 0) return;
    press.current = { mob, from, x: e.clientX, y: e.clientY, started: false };
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = press.current;
      if (!p) return;
      const far = Math.hypot(e.clientX - p.x, e.clientY - p.y) > 6;
      if (!p.started) {
        // 마우스·폰 모두 조금(6px) 움직이면 끌기 시작
        if (far) beginDrag(e.clientX, e.clientY);
        return;
      }
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY, ...dropAt(e.clientX, e.clientY) } : d));
    };
    const up = (e: PointerEvent) => {
      const p = press.current;
      if (!p) return;
      if (!p.started) { press.current = null; return; }   // 짧게 눌렀다 — 말풍선은 onClick 이 띄운다
      justDragged.current = true;
      window.setTimeout(() => { justDragged.current = false; }, 0);
      // 놓을 자리는 press 를 비우기 **전에** 센다 — dropAt 이 끄는 몹 자신을 빼고 세는데, 먼저 비웠더니 자신까지 세어
      // 한 칸 어긋난 자리를 서버에 보냈다(화면은 먼저 옮겼다가 서버 답이 오면 한 칸 다시 움직였다, 사용자 2026-10-31)
      const { to, index } = dropAt(e.clientX, e.clientY);
      press.current = null;
      setDrag(null);
      if (!to || !data) return;
      // 같은 자리에 놓으면 아무것도 안 한다
      const list = data[mode][stars][p.from];
      if (to === p.from && list.findIndex((m) => m.label === p.mob.label) === index) return;
      const before = data;
      const next: TierData = { ...data, [mode]: { ...data[mode], [stars]: moved(data[mode][stars], p.mob.label, to, index) } };
      setData(next);
      saving.current = true;
      fetch('/api/siege-battles/tier-list/move', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, stars, label: p.mob.label, to, index })
      })
        .then((r) => r.json())
        .then((d) => {
          if (!d.success) { setData(before); setToast(d.error || '옮기지 못했습니다'); return; }
          const n = normalize(d);
          keep(n);
          setData(n);
        })
        .catch(() => { setData(before); setToast('옮기지 못했습니다'); })
        .finally(() => { saving.current = false; });
    };
    const cancel = () => {
      const p = press.current;
      press.current = null;
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [data, mode, stars]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * [카톡 공유] — **설탕봇이 길드 단톡방에 보낸다**(사용자 2026-10-31 "카톡 버튼을 누르면 톡봇이 보내도록" → 이도길 방 시험 뒤 "이제 단톡방으로").
   * 서버가 알림을 쌓으면 봇이 20초 안에 지금 보는 표(공격·방어 × 별)의 링크를 보낸다. 링크 미리보기가 표 그림이다.
   */
  const [sharing, setSharing] = useState(false);
  const share = () => {
    if (sharing) return;
    setSharing(true);
    fetch('/api/siege-battles/tier-list/share', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, stars })
    })
      .then((r) => r.json())
      .then((d) => setToast(d?.success ? '카톡으로 보냈어요. 잠시 뒤 방에 올라와요' : d?.error || '보내지 못했습니다'))
      .catch(() => setToast('보내지 못했습니다'))
      .finally(() => setSharing(false));
  };

  // 다른 탭과 같은 짜임: 보라 그라데이션 바탕 · 흰 반투명 카드 · 가운데 제목
  const pad = isMobile ? 'clamp(10px, 2.5vw, 14px)' : 20;
  const card: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)', borderRadius: 20, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    padding: pad, marginBottom: 16, textAlign: 'left'
  };
  const icon = isMobile ? 'clamp(38px, 10.5vw, 46px)' : 52;
  const tiers = data ? data[mode][stars] : null;
  const edited = data?.by ? `${data.by} · ${new Date(data.at).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : '';

  const mobCell = (m: Mob, from: Slot) => {
    const on = picked?.label === m.label;
    const ghost = drag?.mob.label === m.label;
    return (
      <button key={m.label} type="button" title={m.label} data-mob={m.label}
        onPointerDown={(e) => onDown(e, m, from)}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          if (drag || justDragged.current) return;
          // 화면 가장자리 몹은 말풍선이 밖으로 삐지지 않게 그쪽 끝에 붙인다
          const r = e.currentTarget.getBoundingClientRect();
          setSide(r.left < 110 ? 'left' : window.innerWidth - r.right < 110 ? 'right' : 'center');
          setPicked(on ? null : m);
        }}
        style={{
          position: 'relative', padding: 0, border: 'none', background: 'none', cursor: 'grab', lineHeight: 0, zIndex: on ? 2 : undefined,
          opacity: ghost ? 0.3 : 1, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' as any, touchAction: 'none',
          ['--tier-icon' as any]: typeof icon === 'number' ? `${icon}px` : icon
        }}>
        {/*
          고른 몹 = 공략·전투기록 창과 같은 금테 숨쉬기(App.css .mob-picked — 바로 아래 칸 바탕이 금색 테가 된다).
          숨쉬는 빛은 감싸개에만 — 말풍선은 감싸개 밖에 둬서 빛이 안 묻는다(사용자 2026-10-29).
        */}
        <div className={on ? 'mob-picked' : undefined} style={{ borderRadius: 12 }}>
          <div style={{ padding: 3, borderRadius: 12, background: 'transparent' }}>
            <img loading="lazy" alt={m.label} draggable={false}
              src={m.filepath ? `/mobs/v2/${m.filepath.replace(/\\/g, '/')}` : '/images/empty.png'}
              style={{ width: icon, height: icon, borderRadius: 9, objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
          </div>
        </div>
        {on && (
          // 몹 위에 뜨는 말풍선 — 이름·우리 전적
          <i style={{ fontStyle: 'normal',
            position: 'absolute', bottom: 'calc(100% + 8px)',
            ...(side === 'left' ? { left: 0 } : side === 'right' ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' }),
            background: '#2d2a4a', color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1.3,
            padding: '6px 10px', borderRadius: 8, whiteSpace: 'nowrap', textAlign: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            pointerEvents: 'none'
          }}>
            <b style={{ display: 'block', fontSize: 13 }}>{m.label}</b>
            <b style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#d9d4ff', marginTop: 2 }}>
              {m.win + m.lose
                ? `${mode === 'attack' ? '공격' : '방어'} ${m.win}승 ${m.lose}패 · 승률 ${Math.round((m.win / (m.win + m.lose)) * 100)}%`
                : '기록 없음'}
            </b>
            <span style={{
              position: 'absolute', top: '100%',
              // 꼬리는 늘 몹 가운데를 가리킨다
              ...(side === 'left' ? { left: 'calc(var(--tier-icon) / 2 + 3px)', transform: 'translateX(-50%)' }
                : side === 'right' ? { right: 'calc(var(--tier-icon) / 2 + 3px)', transform: 'translateX(50%)' }
                : { left: '50%', transform: 'translateX(-50%)' }),
              borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #2d2a4a'
            }} />
          </i>
        )}
      </button>
    );
  };

  /** 놓을 자리 표시 — 끄는 동안 그 줄의 그 순서에 세로 막대 */
  const marker = (slot: Slot, i: number) =>
    drag && drag.to === slot && drag.index === i
      ? <span key={`mk-${i}`} style={{ width: 4, alignSelf: 'stretch', minHeight: 36, borderRadius: 2, background: PURPLE }} />
      : null;

  const row = (slot: Slot, list: Mob[]) => {
    const out: React.ReactNode[] = [];
    list.forEach((m, i) => {
      // 끄는 몹 자신은 순서에서 빼고 센다(dropAt 과 같게)
      const before = list.slice(0, i).filter((x) => x.label !== drag?.mob.label).length;
      if (m.label !== drag?.mob.label) out.push(marker(slot, before));
      out.push(mobCell(m, slot));
    });
    out.push(marker(slot, list.filter((x) => x.label !== drag?.mob.label).length));
    return out;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? 'clamp(12px, 3vw, 20px) clamp(6px, 2vw, 12px)' : '32px 20px'
    }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ ...card, position: 'relative' }}>
          {/* 카톡 공유 — 공지 탭과 같은 노란 말풍선 아이콘, 머리 카드 오른쪽 위(사용자 2026-10-31) */}
          <button type="button" onClick={share} disabled={sharing} title="카톡으로 보내기" aria-label="카톡으로 보내기" style={{
            opacity: sharing ? 0.5 : 1, position: 'absolute', top: isMobile ? 8 : 12, right: isMobile ? 8 : 12,
            width: 36, height: 36, borderRadius: 8, border: 'none', padding: 0, cursor: 'pointer', background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}><KakaoIcon size={28} /></button>
          <h2 style={{ margin: 0, fontSize: isMobile ? 'clamp(18px, 4vw, 22px)' : 26, fontWeight: 800, color: PURPLE, textAlign: 'center' }}>점령전 티어표</h2>
          <p style={{ margin: '6px 0 0', fontSize: isMobile ? 'clamp(11px, 2.4vw, 12px)' : 14, color: '#666', textAlign: 'center', fontWeight: 500 }}>
            {/* 설명 문구는 사용자 지시 그대로(2026-10-31) */}
            길드원 모두가 수정 가능한 티어표입니다. 몹을 끌어서 옮겨주세요.
          </p>
          {edited && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#999', textAlign: 'center' }}>마지막 수정 {edited}</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Pill on={mode === 'attack'} onClick={() => setMode('attack')}>공격</Pill>
            <Pill on={mode === 'defense'} onClick={() => setMode('defense')}>방어</Pill>
            <span style={{ width: 12 }} />
            <Pill on={stars === 5} onClick={() => setStars(5)}>5성</Pill>
            <Pill on={stars === 4} onClick={() => setStars(4)}>4성</Pill>
            <Pill on={stars === 3} onClick={() => setStars(3)}>2각·3성</Pill>
          </div>
        </div>

        {error && <div style={{ ...card, color: '#d32f2f', fontSize: 14, textAlign: 'center' }}>{error}</div>}
        {!data && !error && <div style={{ ...card, color: '#888', fontSize: 14, textAlign: 'center' }}>불러오는 중…</div>}

        {tiers && (
          <div style={card}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {TIERS.map((t) => (
                // 줄 둘레를 자르지 않는다(overflow) — 고른 몹 위 말풍선이 줄 밖으로 나온다
                <div key={t.key} data-tier-row={t.key} style={{
                  display: 'flex', alignItems: 'stretch', borderRadius: 12, minHeight: 64,
                  background: drag?.to === t.key ? '#e8ebfb' : '#f4f5fb', transition: 'background 0.15s'
                }}>
                  <div style={{
                    width: isMobile ? 44 : 72, flexShrink: 0, background: t.color, color: '#fff', borderRadius: '12px 0 0 12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? 22 : 30, fontWeight: 900
                  }}>{t.key}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, alignItems: 'center', flex: 1 }}>
                    {tiers[t.key].length === 0 && !drag && <span style={{ color: '#aaa', fontSize: 13 }}>없음</span>}
                    {row(t.key, tiers[t.key])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tiers && (
          // 미분류 — 표에 없는 몹(우리 기록에 나온 이 별의 몹). 많아서 접어 둔다. 끄는 중에는 펼쳐 놓을 자리가 된다
          <div style={card}>
            <button type="button" onClick={() => setPoolOpen((v) => !v)} style={{
              width: '100%', border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              fontSize: 15, fontWeight: 800, color: '#4b5375', display: 'flex', justifyContent: 'space-between'
            }}>
              <span>미분류 몹 {tiers.pool.length}</span>
              <span style={{ color: '#999', fontWeight: 600, fontSize: 13 }}>{poolOpen || drag ? '접기' : '펼쳐서 표에 끌어 넣기'}</span>
            </button>
            {(poolOpen || drag) && (
              <div data-tier-row="pool" style={{
                display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, marginTop: 8, borderRadius: 12, minHeight: 56,
                background: drag?.to === 'pool' ? '#e8ebfb' : '#f4f5fb', alignItems: 'center'
              }}>
                {row('pool', tiers.pool)}
              </div>
            )}
          </div>
        )}
      </div>

      {drag && (
        // 손가락·마우스를 따라오는 몹
        <img alt="" src={drag.mob.filepath ? `/mobs/v2/${drag.mob.filepath.replace(/\\/g, '/')}` : '/images/empty.png'} style={{
          position: 'fixed', left: drag.x, top: drag.y, width: 56, height: 56, borderRadius: 10, transform: 'translate(-50%, -60%) scale(1.1)',
          pointerEvents: 'none', zIndex: 9999, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)', border: `2px solid ${PURPLE}`
        }} />
      )}

      {toast && (
        <div style={{
          position: 'fixed', left: '50%', bottom: 32, transform: 'translateX(-50%)', zIndex: 9999,
          background: 'rgba(30, 28, 60, 0.92)', color: '#fff', padding: '10px 16px', borderRadius: 12, fontSize: 14, fontWeight: 600,
          maxWidth: '90vw', textAlign: 'center'
        }}>{toast}</div>
      )}
    </div>
  );
};

export default TierPage;
